import {
  getClassifiedAs,
  normalizeFieldToArray,
  getValueByClassification
} from "@thegetty/linkedart.js";

import aat from "@/data/aat";
import thesaurus from "@/data/internal-ontologies";

const IDENTIFIED_BY = "identified_by";
const EQUIVALENT = "equivalent";
import ulan from "@/data/ulan.json";
const CENTER = "CENTER";
const OTHER = "OTHER";
const VILLA = "VILLA";
import { getName, getPlaceHierarchy } from "@thegetty-private/linked-data.js";

/**
 * Gets the string "Place Name" from the Place LOD
 *
 * @param {object} placeObject - the JSON-LD Place Object
 *
 * @returns {string} - a string with the Pleace's place name
 *
 */
export function getExhibitionPlaceName(placeObject) {
  const identifiedBy = normalizeFieldToArray(placeObject, IDENTIFIED_BY);

  const placeName = getValueByClassification(identifiedBy, aat.PLACE_NAMES);

  return placeName;
}

/**
 * Gets the string "Venue Name" from the Place LOD
 *
 * @param {object} placeObject - the JSON-LD Place Object
 *
 * @returns {string} - a string with the Pleace's place name
 *
 */
export function getExhibitionVenueName(placeObject) {
  const identifiedBy = normalizeFieldToArray(placeObject, IDENTIFIED_BY);

  const placeName = getValueByClassification(identifiedBy, aat.COMMON_NAMES);

  return placeName;
}

/**
 * Gets the building name if it exists from a Place object
 *
 * @param {object} placeObject - the LOD Place object
 *
 * @return {string|undefined} the place object building name
 */
export function getBuildingName(placeObject) {
  return getNameByClassification(placeObject, thesaurus.BUILDING_MNEMONIC);
}

/**
 * Gets the Floor name if it exists from a Place object
 *
 * @param {object} placeObject - the LOD Place object
 *
 * @return {string|undefined} the place object floor name
 */
export function getFloorName(placeObject) {
  return getNameByClassification(placeObject, thesaurus.FLOOR_NAME);
}

/**
 * Gets the room name if it exists from a Place object
 *
 * @param {object} placeObject - the LOD Place object
 *
 * @return {string|undefined} the place object room name
 */
export function getRoomName(placeObject) {
  return getNameByClassification(placeObject, thesaurus.ROOM_NAME);
}

/**
 * Gets the content of an object from a Place's "identified_by" array by its classification.
 *
 * @param {object} placeObject - the LOD Place object
 *
 * @return {array} an array of all the IDs available in "part_of"
 */
export function getNameByClassification(placeObject, classification) {
  if (placeObject) {
    return getValueByClassification(placeObject[IDENTIFIED_BY], classification);
  }
}

/**
 * Checks whether or not the Place is a Pavilion
 *
 * @param {object} placeObject - the LOD Place object
 *
 * @return {boolean}
 */
export function isPavilion(placeObject) {
  return getClassifiedAs(placeObject, aat.PAVILION).length > 0;
}

/**
 * This function returns whether or not the place is at the Getty Center
 *
 * @param {object} placeObject - the LOD Place object
 *
 * @return {boolean} A boolean representing whether or not the place object is at
 * the Getty Center
 */
export function getCenterBoolean(placeObject) {
  return getGalleryLocation(placeObject) == CENTER;
}

/**
 * This function returns the root id item from the hierarchy
 *
 * @param {object} placeObject - the LOD Place object
 *
 * @return {string} the root Id
 */

export function getRootLocation(placeObject) {
  const hierarchy = getPlaceHierarchy(placeObject);
  return hierarchy[hierarchy.length - 1];
}

/**
 * This function returns whether or not the place is at the Getty Villa
 *
 * @param {object} placeObject - the LOD Place object
 *
 * @return {boolean} A boolean representing whether or not the place object is at
 * the Getty Villa
 */
export function getVillaBoolean(placeObject) {
  return getGalleryLocation(placeObject) == VILLA;
}

/**
 * This function returns whether or not the place is at the Getty Villa or Getty Center
 *
 * @param {object} placeObject - the LOD Place object
 *
 * @return {boolean} A boolean representing whether or not the place object is at
 * the Getty Villa or Getty Center
 */
export function getOnViewBoolean(placeObject) {
  return getVillaBoolean(placeObject) || getCenterBoolean(placeObject);
}

/**
 * This function returns true if the placeObject provided is classified as a
 * gallery
 *
 * @param {object} placeObject - the JSON-LD Place Object
 *
 * @return {boolean} A boolean representing whether or not the place is a gallery
 */
export function isPlaceGallery(placeObject) {
  return getClassifiedAs(placeObject, aat.GALLERY).length > 0;
}

/**
 * Extracts the proper formalization of the location name for an Exhibition Location
 *
 * @param {object} placeObject - the JSON-LD Place Object
 *
 * @returns {string} - a string with the institution's name combined with the place name
 */
export function getExhibitionNameWithPlace(placeObject) {
  let placeName = getName(placeObject);
  let identifiedBy = normalizeFieldToArray(placeObject, IDENTIFIED_BY);
  let institutionName = getValueByClassification(
    identifiedBy,
    aat.COMMON_NAMES
  );
  let municipality = getValueByClassification(identifiedBy, aat.PLACE_NAMES);
  if (institutionName && municipality) {
    if (institutionName.endsWith(municipality)) {
      return institutionName;
    }
    placeName = institutionName + " (" + municipality + ")";
  }
  return placeName;
}

/**
 * This function returns whether or not the parent gallery location is the Getty Villa or Getty Center or other
 *
 * @param {object} placeObject - the top level LOD Place object
 *
 * @return {string} (i.e. "CENTER", "VILLA")
 */
export function getGalleryLocation(placeObject) {
  if (
    normalizeFieldToArray(placeObject, EQUIVALENT).filter(
      (item) =>
        item.exact_match &&
        [ulan.GETTY_RESEARCH_INSTITUTE, ulan.GETTY_CENTER].includes(
          item.exact_match[0].id
        )
    ).length > 0
  ) {
    return CENTER;
  }

  if (
    normalizeFieldToArray(placeObject, EQUIVALENT).filter(
      (item) => item.exact_match && item.exact_match[0].id == ulan.GETTY_VILLA
    ).length > 0
  ) {
    return VILLA;
  }

  return OTHER;
}

/**
 * This function returns what Pavilion a Getty Center gallery is in
 *
 * @param {object} placeObject - the LOD Place object
 *
 * @return {string} (i.e. "West")
 */
export function getGalleryPavilion(placeObject) {
  const identifiedBy = normalizeFieldToArray(placeObject, IDENTIFIED_BY);
  return getValueByClassification(identifiedBy, thesaurus.PAVILION_NAME);
}
